import { dayOfDateOnly, type Day } from './calendar';
import { businessZone } from './zone';
import { zonedParts, type ZonedParts } from './zoned';

// Formato canónico (04-GC-P08, Contenido del documento c y d): fecha dd/mm/aaaa
// ("31/05/2019") y hora de 12 horas sin puntos ("7:00 am", "12:00 pm"). Fechas puras
// por su día (nunca se corren); instantes en la zona de negocio. Se arma con partes
// numéricas: no depende del ICU del navegador. Vacío o inválido: "—".

export const EMPTY_DATE = '—';

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const pad2 = (n: number): string => String(n).padStart(2, '0');

function partsOf(iso: string | null): ZonedParts | null {
  return iso === null ? null : zonedParts(new Date(iso), businessZone());
}

function clock(p: ZonedParts, seconds: boolean): string {
  const h12 = p.h % 12 === 0 ? 12 : p.h % 12;
  const secs = seconds ? `:${pad2(p.s)}` : '';
  return `${h12}:${pad2(p.min)}${secs} ${p.h < 12 ? 'am' : 'pm'}`;
}

/** Día `AAAA-MM-DD` como dd/mm/aaaa. */
export function formatDay(day: Day | null): string {
  const p = day === null ? null : dayOfDateOnly(day);
  if (p === null) {
    return EMPTY_DATE;
  }
  const [y, m, d] = p.split('-');
  return `${d ?? ''}/${m ?? ''}/${y ?? ''}`;
}

/** Fecha pura del API (`@db.Date`) como dd/mm/aaaa. */
export function formatDate(dateOnly: string | null): string {
  return formatDay(dateOnly === null ? null : dayOfDateOnly(dateOnly));
}

/** Día de negocio de un instante como dd/mm/aaaa. */
export function formatInstantDate(iso: string | null): string {
  const p = partsOf(iso);
  return p === null ? EMPTY_DATE : `${pad2(p.d)}/${pad2(p.m)}/${p.y}`;
}

/** Hora de negocio de un instante: "7:00 am"; con segundos, "7:00:09 am". */
export function formatTime(iso: string | null, opts: { seconds?: boolean } = {}): string {
  const p = partsOf(iso);
  return p === null ? EMPTY_DATE : clock(p, opts.seconds === true);
}

/** Fecha y hora de negocio: "15/10/2026 7:05 pm"; con segundos, "7:05:09 pm". */
export function formatDateTime(iso: string | null, opts: { seconds?: boolean } = {}): string {
  const p = partsOf(iso);
  return p === null
    ? EMPTY_DATE
    : `${pad2(p.d)}/${pad2(p.m)}/${p.y} ${clock(p, opts.seconds === true)}`;
}

/** Día y mes compacto de un instante (insignias): "12/07". */
export function formatDayMonth(iso: string | null): string {
  const p = partsOf(iso);
  return p === null ? EMPTY_DATE : `${pad2(p.d)}/${pad2(p.m)}`;
}

export type PeriodGranularity = 'day' | 'month' | 'year';

/** Etiqueta del período de un gráfico: día "05/10", mes "oct 2026", año "2026". */
export function formatPeriod(period: string, granularity: PeriodGranularity): string {
  if (granularity === 'day') {
    const day = dayOfDateOnly(period);
    return day === null ? period : `${day.slice(8, 10)}/${day.slice(5, 7)}`;
  }
  if (granularity === 'month') {
    const m = /^(\d{4})-(\d{2})$/.exec(period);
    const name = m ? MONTHS[Number(m[2]) - 1] : undefined;
    return m && name ? `${name} ${m[1] ?? ''}` : period;
  }
  return period;
}
